import { AgingBucket, BucketSummary, BuyerCheckAgingRow } from 'services/reportsApi';

export const AGING_BUCKETS: AgingBucket[] = ['0-30', '31-60', '61-90', '90+', 'unmailed'];

export type BucketCells = Record<AgingBucket, BucketSummary>;

export interface VendorPivotRow {
  account_id: string | null;
  vendor_name: string;
  buckets: BucketCells;
  total: BucketSummary;
  rows: BuyerCheckAgingRow[];
}

const emptyCells = (): BucketCells => ({
  '0-30': { count: 0, total_amount: 0 },
  '31-60': { count: 0, total_amount: 0 },
  '61-90': { count: 0, total_amount: 0 },
  '90+': { count: 0, total_amount: 0 },
  unmailed: { count: 0, total_amount: 0 },
});

const toAmount = (raw: string): number => {
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
};

export const summarizeBuckets = (rows: BuyerCheckAgingRow[]): BucketCells => {
  const summary = emptyCells();
  for (const row of rows) {
    const bucket = summary[row.aging_bucket];
    bucket.count += 1;
    bucket.total_amount += toAmount(row.amount);
  }
  return summary;
};

const UNMAPPED_VENDOR_KEY = '__unmapped__';

export const groupByVendor = (rows: BuyerCheckAgingRow[]): VendorPivotRow[] => {
  const byVendor = new Map<string, VendorPivotRow>();

  for (const row of rows) {
    const key = row.account_id ?? UNMAPPED_VENDOR_KEY;
    let entry = byVendor.get(key);
    if (!entry) {
      entry = {
        account_id: row.account_id,
        vendor_name: row.vendor_name ?? 'Unknown vendor',
        buckets: emptyCells(),
        total: { count: 0, total_amount: 0 },
        rows: [],
      };
      byVendor.set(key, entry);
    }
    const amount = toAmount(row.amount);
    const cell = entry.buckets[row.aging_bucket];
    cell.count += 1;
    cell.total_amount += amount;
    entry.total.count += 1;
    entry.total.total_amount += amount;
    entry.rows.push(row);
  }

  // Sort worst-aging first: 90+ count DESC, then total count DESC.
  return Array.from(byVendor.values()).sort((a, b) => {
    const byNinety = b.buckets['90+'].count - a.buckets['90+'].count;
    if (byNinety !== 0) return byNinety;
    return b.total.count - a.total.count;
  });
};

export const uniqueProperties = (
  rows: BuyerCheckAgingRow[]
): Array<{ id: string; name: string }> => {
  const seen = new Map<string, string>();
  for (const row of rows) {
    if (!seen.has(row.property_id)) {
      seen.set(row.property_id, row.property_name ?? 'Unknown property');
    }
  }
  return Array.from(seen.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
};
